import compareSettings from '~/settings/compareSettings';
export const RULE_NAMES = {
    1: 'Exact classification',
    2: 'Parent is an exact match',
    3: 'Child is an exact match',
    4: 'Descriptor exact match in category family',
    5: 'Descriptor parent in category family',
    6: 'Descriptor child in category family',
    7: 'Semantic similarity (embedding)',
};
export const isAlignmentDebugOn = () => typeof window !== 'undefined' && localStorage.getItem('debug-logging') === 'true';
const NEAR_MISS_BAND = 0.1;
const round = (value) => Math.round(value * 100) / 100;
const floor3 = (value) => Math.floor(value * 1000) / 1000;
const ceil3 = (value) => Math.ceil(value * 1000) / 1000;
const pct = (part, whole) => (whole === 0 ? 0 : round((part / whole) * 100));
const emptyDocReport = (fileName, isAnchor) => ({
    fileName,
    isAnchor,
    totalClauses: 0,
    tagMatched: 0,
    similarityMatched: 0,
    unmatched: 0,
    withEmbedding: 0,
    withoutEmbedding: 0,
});
const logAlignmentReport = (outcomes, useSimilarity) => {
    var _a;
    if (!isAlignmentDebugOn())
        return;
    const perDocumentMap = new Map();
    const ruleCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
    const similarityMatches = [];
    const similarityScores = [];
    const unmatchedClauses = [];
    const belowThresholdNearMisses = [];
    const threshold = compareSettings.similarityThreshold;
    let clausesWithEmbedding = 0;
    let anchorClauses = 0;
    let anchorWithEmbedding = 0;
    let comparatorClauses = 0;
    let comparatorWithEmbedding = 0;
    let comparatorTagMatched = 0;
    let comparatorSimilarityMatched = 0;
    let comparatorUnmatched = 0;
    let comparatorUnmatchedWithEmbedding = 0;
    let comparatorUnmatchedWithoutEmbedding = 0;
    outcomes.forEach(outcome => {
        const doc = perDocumentMap.get(outcome.documentComparisonFileId)
            || emptyDocReport(outcome.fileName, outcome.isAnchor);
        doc.totalClauses += 1;
        if (outcome.hasEmbedding) {
            doc.withEmbedding += 1;
            clausesWithEmbedding += 1;
        }
        else {
            doc.withoutEmbedding += 1;
        }
        const isTagMatch = outcome.rule !== undefined && outcome.rule >= 1 && outcome.rule <= 6;
        const isSimilarityMatch = outcome.rule === 7;
        if (isTagMatch)
            doc.tagMatched += 1;
        else if (isSimilarityMatch)
            doc.similarityMatched += 1;
        else
            doc.unmatched += 1;
        if (outcome.rule !== undefined)
            ruleCounts[outcome.rule] += 1;
        if (outcome.isAnchor) {
            anchorClauses += 1;
            if (outcome.hasEmbedding)
                anchorWithEmbedding += 1;
        }
        else {
            comparatorClauses += 1;
            if (outcome.hasEmbedding)
                comparatorWithEmbedding += 1;
            if (isTagMatch)
                comparatorTagMatched += 1;
            else if (isSimilarityMatch)
                comparatorSimilarityMatched += 1;
            else
                comparatorUnmatched += 1;
            if (isSimilarityMatch) {
                similarityMatches.push({
                    document: outcome.fileName,
                    clause: outcome.name,
                    matchedRow: outcome.matchedRowKey,
                    score: outcome.similarityScore !== undefined ? round(outcome.similarityScore) : undefined,
                });
                if (outcome.similarityScore !== undefined)
                    similarityScores.push(outcome.similarityScore);
            }
            if (outcome.rule === undefined) {
                if (outcome.hasEmbedding)
                    comparatorUnmatchedWithEmbedding += 1;
                else
                    comparatorUnmatchedWithoutEmbedding += 1;
                unmatchedClauses.push({
                    document: outcome.fileName,
                    clause: outcome.name,
                    category: outcome.category,
                    hasEmbedding: outcome.hasEmbedding,
                });
                if (outcome.nearMiss && outcome.nearMiss.score >= threshold - NEAR_MISS_BAND) {
                    belowThresholdNearMisses.push({
                        document: outcome.fileName,
                        clause: outcome.name,
                        category: outcome.category,
                        closestAnchor: outcome.nearMiss.anchorName,
                        score: floor3(outcome.nearMiss.score),
                        threshold,
                        gap: ceil3(threshold - outcome.nearMiss.score),
                    });
                }
            }
        }
        perDocumentMap.set(outcome.documentComparisonFileId, doc);
    });
    belowThresholdNearMisses.sort((a, b) => b.score - a.score);
    const totalClauses = outcomes.length;
    const anchorDoc = outcomes.find(outcome => outcome.isAnchor);
    const ruleBreakdown = Object.keys(RULE_NAMES)
        .map(Number)
        .map(rule => ({ rule, name: RULE_NAMES[rule], count: ruleCounts[rule] }));
    const similarityScoreStats = {
        count: similarityScores.length,
        min: similarityScores.length ? round(Math.min(...similarityScores)) : null,
        max: similarityScores.length ? round(Math.max(...similarityScores)) : null,
        average: similarityScores.length
            ? round(similarityScores.reduce((sum, score) => sum + score, 0) / similarityScores.length)
            : null,
    };
    const report = {
        label: 'Clause Comparison alignment report',
        summary: {
            documents: perDocumentMap.size,
            anchorDocument: (_a = anchorDoc === null || anchorDoc === void 0 ? void 0 : anchorDoc.fileName) !== null && _a !== void 0 ? _a : '(unknown)',
            similarityAlignmentEnabled: useSimilarity,
            similarityThreshold: threshold,
            totalClauses,
            comparatorClauses,
        },
        embeddingCoverage: {
            overallPct: pct(clausesWithEmbedding, totalClauses),
            anchor: { withEmbedding: anchorWithEmbedding, total: anchorClauses,
                pct: pct(anchorWithEmbedding, anchorClauses) },
            comparator: { withEmbedding: comparatorWithEmbedding, total: comparatorClauses,
                pct: pct(comparatorWithEmbedding, comparatorClauses) },
        },
        comparatorOutcomes: {
            tagMatched: comparatorTagMatched,
            tagMatchedPct: pct(comparatorTagMatched, comparatorClauses),
            similarityMatched: comparatorSimilarityMatched,
            similarityMatchedPct: pct(comparatorSimilarityMatched, comparatorClauses),
            unmatched: comparatorUnmatched,
            unmatchedPct: pct(comparatorUnmatched, comparatorClauses),
            unmatchedWithEmbedding: comparatorUnmatchedWithEmbedding,
            unmatchedWithoutEmbedding: comparatorUnmatchedWithoutEmbedding,
        },
        ruleBreakdown,
        similarityScoreStats,
        belowThresholdNearMisses,
        perDocument: Array.from(perDocumentMap.values()),
        similarityMatches,
        unmatchedClauses,
    };
    window.debugLogger(report);
};
export default logAlignmentReport;
